import React, { useState } from 'react';
import {
	useLazyWorkplaceCaseTypesQuery,
	useLazyWorkplaceDoctorsQuery,
} from '../../../../graphql/graphql';
import { SelectValue } from '../../../../Components/Base/Select/Select';
import { FieldError } from 'react-hook-form';
import { useAppContext } from '../../../../Services/AppContext';
import { useEffectAsync } from '../../../../helpers';
import MultiSelectWithCheckList from '../../../Base/Components/Inputs/MultiSelectWithCheckList/MultiSelectWithCheckList';

interface Props {
	onChange?: (value: SelectValue) => void;
	workplaceId?: string;
	value?: SelectValue;
	error?: FieldError;
	placeholder?: string;
	portal?: boolean;
	allowNotSpecified?: boolean;
	onlyDoctorsAndMe?: boolean;
	roleKeys?: string[];
}

const DoctorMultiSelectWithCheckList: React.FC<Props> = props => {
	const {
		value,
		onChange,
		workplaceId,
		portal,
		allowNotSpecified = false,
		onlyDoctorsAndMe = false,
		roleKeys = ['doctor', 'nurse'],
	} = props;
	const [defaultValue, setDefaultValue] = useState<SelectValue | undefined>(
		value,
	);
	const [isReady, setIsReady] = useState(false);
	const [options, setOptions] = useState<SelectValue[]>();
	const [getWorkplaceDoctors] = useLazyWorkplaceDoctorsQuery();
	const { userData } = useAppContext();
	const currentWorkplaceId = workplaceId ?? userData.workplaceId;

	useEffectAsync(async () => {
		setIsReady(isReady);
		const items: SelectValue[] = [];

		if (allowNotSpecified) {
			items.push({
				value: '00000000-0000-0000-0000-000000000000',
				label: 'base.shared.notSpecified',
			});
		}

		const { workplaceDoctors } = await getWorkplaceDoctors({
			workplaceId: currentWorkplaceId,
			roleKeys,
			take: 999,
		}).unwrap();

		for (const workplaceDoctor of workplaceDoctors?.data || []) {
			if (workplaceDoctor && workplaceDoctor.user) {
				if (onlyDoctorsAndMe) {
					const isDoctor = workplaceDoctor?.workplaceMemberRoles?.find(
						item => item?.role?.key === 'doctor',
					);
					if (isDoctor || workplaceDoctor.user.id === userData.id) {
						if (workplaceDoctor.user.profile?.fullName) {
							items.push({
								value: workplaceDoctor.user.id,
								label: workplaceDoctor.user.profile.fullName,
							});
						}
					}
				} else {
					if (workplaceDoctor.user.profile?.fullName) {
						items.push({
							value: workplaceDoctor.user.id,
							label: workplaceDoctor.user.profile.fullName,
						});
					}
				}
			}
		}

		setOptions(items);
		setIsReady(true);
	}, [workplaceId]);

	if (isReady) {
		return (
			<MultiSelectWithCheckList
				{...props}
				options={options}
				onChange={onChange}
				value={defaultValue?.label ? defaultValue : value}
				portal={portal}
			/>
		);
	}

	return null;
};

export default DoctorMultiSelectWithCheckList;
